import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { firstEnabledIndex, isRovingKey, resolveRovingIndex } from '../components/rovingFocus';
import { usePopoverClamp } from '../components/usePopoverClamp';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import type { ToolAction } from './ToolsMenu';

/**
 * Menu de DÉBORDEMENT mobile (suivi planifié de v0.57.0) : sur petit écran, la barre
 * d'outils masque le menu « Outils ». Ce menu (visible `sm:hidden`, masqué dès `sm` où le
 * menu Outils réapparaît) rassemble ces entrées inaccessibles au mobile : un éventuel lien
 * de navigation en tête (avec son badge de compteur) PUIS toutes les actions du menu Outils
 * (mêmes handlers, réutilisés tels quels).
 *
 * Reprend l'accessibilité de `ToolsMenu` (rôle `menu`/`menuitem`, flèches ↑/↓/Home/End,
 * Échap ferme + rend le focus au déclencheur, clic extérieur ferme, Tab ferme). Le premier
 * item est un `Link` de navigation (react-router) SI `navItem` est fourni ; les suivants
 * sont les actions Outils. Tous participent à la navigation clavier (index unique couvrant
 * l'éventuel lien + les actions).
 */

/** Entrée de navigation (lien react-router), avec badge optionnel. */
export interface OverflowNavItem {
  /** Libellé accessible + visible. */
  label: string;
  /** Cible de navigation (chemin react-router). */
  to: string;
  /** Compteur de badge (0/omis ⇒ pas de badge). */
  badgeCount?: number;
  /** Gabarit i18n du libellé accessible du badge (`{count}`). */
  badgeLabelTemplate?: string;
}

interface OverflowMenuProps {
  trigger: React.ReactNode;
  /** Libellé accessible du déclencheur (aria-label + title). */
  label: string;
  /** Libellé de fermeture (a11y), fourni par i18n. */
  closeLabel: string;
  /** Item de navigation optionnel en tête (lien + badge). Absent ⇒ que les actions Outils. */
  navItem?: OverflowNavItem;
  /** Actions du menu Outils (mêmes objets que le menu Outils desktop). */
  actions: readonly ToolAction[];
}

export function OverflowMenu({
  trigger,
  label,
  closeLabel,
  navItem,
  actions,
}: OverflowMenuProps): React.JSX.Element {
  const { locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Références de TOUS les items (lien de nav à l'index 0, puis les actions).
  const itemsRef = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  // Maintien dans le cadre visible — ce menu n'existe QUE sous `sm`, là où le défaut
  // se manifeste (mesuré à −148 px sur 360 px de large).
  const panelRef = usePopoverClamp(open);

  // Décalage d'index dû à l'éventuel lien de nav en tête (0 s'il est absent).
  const navOffset = navItem ? 1 : 0;

  /**
   * Masque d'activabilité de TOUS les items (lien de nav toujours activable, puis les
   * actions) — les entrées désactivées sont ENJAMBÉES par les flèches.
   */
  const enabled = useMemo(
    () => [...(navOffset === 1 ? [true] : []), ...actions.map((a) => a.disabled !== true)],
    [navOffset, actions],
  );

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    // Ouvre en focalisant le premier item ACTIVABLE (lien de nav s'il existe).
    setActiveIndex(firstEnabledIndex(enabled));
    setOpen(true);
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  useEffect(() => {
    if (open) itemsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const onButtonKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    if (!isRovingKey(e.key, 'vertical')) return;
    e.preventDefault();
    // `null` = menu ENTIÈREMENT désactivé : on ne bouge pas (et surtout, pas de boucle).
    const next = resolveRovingIndex(e.key, activeIndex, enabled, 'vertical');
    if (next !== null) setActiveIndex(next);
  };

  const runAction = (action: ToolAction): void => {
    if (action.disabled) return;
    action.onSelect?.();
    close();
  };

  const showBadge = navItem !== undefined && (navItem.badgeCount ?? 0) > 0;

  return (
    <div ref={rootRef} className="relative sm:hidden">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onButtonKeyDown}
        className="relative flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
      >
        {trigger}
        {/* Badge du lien de nav reporté sur le déclencheur (l'entrée est dans le menu). */}
        {showBadge && (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand px-1 py-px text-[0.625rem] font-semibold leading-none text-brand-fg"
            aria-hidden="true"
          >
            {navItem?.badgeCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          id={menuId}
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className="absolute end-0 z-20 mt-1 min-w-52 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {/* Lien de navigation optionnel (react-router) — item 0 s'il est présent. Le nom
              accessible est porté par le lien LUI-MÊME (comme `GalleryTabsBar`) et inclut le
              compteur ; le badge visuel est `aria-hidden` (le compteur passe par le nom). */}
          {navItem && (
            <Link
              ref={(el) => {
                itemsRef.current[0] = el;
              }}
              to={navItem.to}
              role="menuitem"
              aria-label={
                showBadge && navItem.badgeLabelTemplate
                  ? `${navItem.label} — ${interpolate(
                      navItem.badgeLabelTemplate,
                      {
                        count: navItem.badgeCount ?? 0,
                      },
                      locale,
                    )}`
                  : navItem.label
              }
              tabIndex={activeIndex === 0 ? 0 : -1}
              onClick={() => close(false)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
            >
              <span className="flex-1">{navItem.label}</span>
              {showBadge && (
                <span
                  className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand px-1.5 py-0.5 text-xs font-semibold leading-none text-brand-fg"
                  aria-hidden="true"
                >
                  {navItem.badgeCount}
                </span>
              )}
            </Link>
          )}
          {/* Actions du menu Outils — items `navOffset`..n (mêmes handlers/état que le menu Outils). */}
          {actions.map((action, index) => (
            <button
              key={action.id}
              ref={(el) => {
                itemsRef.current[index + navOffset] = el;
              }}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              tabIndex={index + navOffset === activeIndex ? 0 : -1}
              onClick={() => runAction(action)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:text-muted disabled:opacity-60 motion-reduce:transition-none"
            >
              <span className="flex-1">{action.label}</span>
            </button>
          ))}
          <span className="sr-only">{closeLabel}</span>
        </div>
      )}
    </div>
  );
}
